import React from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { DeviceFormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import JarvisConnectionForm from "./JarvisConnectionForm";
import {
  STATUS_IDS,
  STATUS_LABELS,
  ACTION_IDS,
  TableEmptyMessage,
  CONNECTION_STATUS_LABELS,
  statusIsEditable,
  isSet,
} from "../../../../components/Utils";
import {
  PermissionResource,
  PermissionSection,
  Resources,
} from "../../../Permissions";
import {
  getITConnectionFormFlags,
  getJarvisPermissionLabel,
  normalizeJarvisPermission,
} from "../Utils";

function generateState(model, readOnly) {
  return {
    items: (model ? [model] : []).map((item, index) => ({ ...item, index })),
    addDisabled: readOnly,
    localStateChanged: false,
  };
}

function isSame(connectionA, connectionB) {
  return (
    connectionA &&
    connectionB &&
    normalizeJarvisPermission(connectionA.permissions) ===
      normalizeJarvisPermission(connectionB.permissions) &&
    connectionA.externalEmail === connectionB.externalEmail
  );
}

function JarvisConnectionListForm({
  forwardedRef,
  id = "jarvisConnectionListForm",
  className,
  kdlCompanyList,
  model = null,
  getCompanyData,
  getEmployeeData,
  disabled: isDisabled,
  readOnly = false,
  isDL = false,
}) {
  const formRef = React.useRef(null);
  const disabled = React.useMemo(
    () => isDisabled || readOnly,
    [isDisabled, readOnly],
  );
  const [state, setState] = React.useState(generateState(model, disabled));

  React.useEffect(() => {
    setState((prev) => ({
      ...prev,
      addDisabled: disabled,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const getModel = React.useCallback(() => {
    const { items } = state;
    return items[0] || null;
  }, [state]);

  React.useImperativeHandle(forwardedRef, () => ({ getModel }));

  const onSave = React.useCallback(
    (onHide, updating) => {
      const currentModel = formRef.current.getModel();
      if (!currentModel) {
        return;
      }
      currentModel.status_id = updating ? STATUS_IDS.UPDATING : STATUS_IDS.NEW;
      currentModel.action_id = updating ? ACTION_IDS.UPDATE : ACTION_IDS.CREATE;
      currentModel.status = CONNECTION_STATUS_LABELS[currentModel.status_id];
      const { items } = state;
      let localStateChanged = false;
      if (
        currentModel.index !== undefined &&
        items[currentModel.index] !== undefined
      ) {
        if (model && isSame(model, currentModel)) {
          // no local changes made
          currentModel.action_id = model.action_id;
          currentModel.status_id = model.status_id;
          currentModel.status = model.status;
        } else {
          // local changes made
          localStateChanged = true;
        }
        items[currentModel.index] = currentModel;
      } else {
        currentModel.index = items.length;
        items.push(currentModel);
      }
      setState((prev) => ({ ...prev, items, localStateChanged }));
      onHide && onHide();
    },
    [formRef, state, model],
  );

  const onDelete = React.useCallback(
    (selected) => {
      if (selected.id) {
        const { items } = state;
        if (items[selected.index]) {
          items[selected.index].status_id = STATUS_IDS.DELETING;
          items[selected.index].action_id = ACTION_IDS.DELETE;
          items[selected.index].status = null; // [STATUS_IDS.DELETING];
          setState((prev) => ({ ...prev, items, localStateChanged: true }));
          // onChange && onChange(items);
        } else {
          console.error(
            "<< jarvis connection to be deleted was not found",
            state.items,
          );
        }
      } else {
        // delete local changes quietly
        const items = state.items.filter((_, i) => i !== selected.index);
        setState((prev) => ({ ...prev, items }));
        // onChange && onChange(items);
      }
    },
    [state],
  );

  const isContractor = React.useCallback(() => {
    let value = true;
    const companyData = getCompanyData();
    if (companyData && companyData.isContractor !== undefined) {
      value = companyData.isContractor;
    }
    return value;
  }, [getCompanyData]);

  const defaultExternalEmail = React.useMemo(() => {
    const employeeData = getEmployeeData();
    return model && model.externalEmail !== ""
      ? model.externalEmail
      : employeeData.email;
  }, [model, getEmployeeData]);

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      console.log("renderItemTable()", items);
      const data = items.map((item, index) => ({
        ...item,
        index,
        status: STATUS_LABELS[item.status_id || STATUS_IDS.NEW],
      }));
      return (
        <DataTable
          value={data}
          rows={1}
          emptyMessage={
            <TableEmptyMessage prefix="Kein" itemNamePlural="JARVIS-Zugang" min />
          }
        >
          <Column field="parentCompanyName" header="Vertragspartner" />
          <Column
            field="permissions"
            header="Rechte"
            body={(rowData) => getJarvisPermissionLabel(rowData.permissions)}
          />
          <Column field="status" header="Status" style={{ width: "16em" }} />
          <Column
            body={actionTemplate}
            style={{ textAlign: "center", width: "8em" }}
          />
        </DataTable>
      );
    },
    [state, disabled],
  );

  const isEditDisabled = React.useCallback(
    (item) => {
      if (
        state.localStateChanged ||
        !isSet(item.id) ||
        item.status_id !== model.status_id
      ) {
        return false;
      }
      return !statusIsEditable(item.status_id, item.id, true) || disabled;
    },
    [state, disabled, model],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const connection = item || {
        parent_company_id: kdlCompanyList.length > 0 ? kdlCompanyList[0].id : 0,
        username: "",
        permissions: "",
        status_id: STATUS_IDS.NEW,
      };
      // const saveDisabled = connection.status_id !== STATUS_IDS.NEW;
      // const updateDisabled =
      //   !isSet(connection.id) || isEditDisabled(connection); // connection.status_id !== STATUS_IDS.FINISHED && !state.localStateChanged;
      const { saveDisabled, updateDisabled } = getITConnectionFormFlags(connection, isEditDisabled);
      // const hasLocalChanges = !isSame(model, connection);
      return (
        <div>
          <JarvisConnectionForm
            ref={formRef}
            model={connection}
            kdlCompanyList={kdlCompanyList}
            readOnly={disabled || (updateDisabled && saveDisabled)}
            defaultExternalEmail={defaultExternalEmail}
            isDL={isDL}
            key={Math.random()}
          />
          <DeviceFormMenu
            className="margin-big-top w3-border-top pad-big w3-light-grey"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onUpdate={() => onSave(onHide, true)}
            onCancel={onHide}
            saveDisabled={saveDisabled}
            updateDisabled={updateDisabled/*  || !hasLocalChanges */}
            saveHidden={disabled || saveDisabled}
            updateHidden={disabled || updateDisabled}
            saveBtnLabel="Beantragen"
            updateBtnLabel="Ändern"
            cancelBtnLabel={
              disabled || (updateDisabled && saveDisabled)
                ? "Schließen"
                : undefined
            }
            saveBtnName="saveUserJarvisAccBtn"
            updateBtnName="updateUserJarvisAccBtn"
            cancelBtnName="cancelUserJarvisAccBtn"
          />
        </div>
      );
    },
    [
      state,
      kdlCompanyList,
      // isContractor,
      defaultExternalEmail,
      disabled,
      isEditDisabled,
      isDL,
    ],
  );

  const isDeleteDisabled = React.useCallback(
    (item) => {
      if (
        isSet(item.id) &&
        model &&
        model[item.index] &&
        item.status_id !== model[item.index].status_id &&
        item.status_id !== STATUS_IDS.DELETING
      ) {
        // if (isSet(item.id) || (model && item.status_id !== model.status_id && item.status_id !== STATUS_IDS.DELETING)) {
        return false;
      }
      return !statusIsEditable(item.status_id, item.id) || disabled;
    },
    [disabled, model],
  );

  const getItemFormTitle = React.useCallback(
    (connection) => {
      let action = "bearbeiten";
      if (!connection) {
        action = "anlegen";
      } else if (isEditDisabled(connection)) {
        action = "ansehen";
      }
      return <h3 className="no-margin">{`JARVIS-Zugang ${action}`}</h3>;
    },
    [isEditDisabled],
  );

  return (
    <>
      <ItemListManager
        id={id}
        key={Math.random()}
        itemPanelStyle={{ width: "480px" }}
        className={className}
        title={
          <h4 className="no-margin-top" style={{ height: "36px" }}>
            {"JARVIS-Zugang"}
          </h4>
        }
        addBtnLabel="Hinzufügen"
        addBtnName="addWorkerJarvisConnectionBtn"
        addBtnDisabled={disabled || state.addDisabled}
        addBtnHidden={state.items.length === 1}
        items={state.items}
        getItemFormTitle={getItemFormTitle}
        renderItemTable={renderItemTable}
        renderItemForm={renderItemForm}
        namePrefix="jarvisConnection"
        isEditDisabled={isEditDisabled}
        itemDeleteable={!disabled}
        hideDisabledDelete
        isDeleteDisabled={isDeleteDisabled}
        deleteTitle="JARVIS-Zugang löschen"
        getDeleteText={(item) =>
          `Wollen Sie den JARVIS-Zugang (Vertragspartner: ${item.parentCompanyName}) unwiderruflich löschen?`
        }
        onDelete={onDelete}
      />
    </>
  );
}
JarvisConnectionListForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    permissions: PropTypes.string,
    externalEmail: PropTypes.string,
  }),
  kdlCompanyList: PropTypes.array,
  readOnly: PropTypes.bool,
  canReadAdminData: PropTypes.bool,
  disabled: PropTypes.bool,
  getCompanyData: PropTypes.func.isRequired,
  getEmployeeData: PropTypes.func.isRequired,
};

export default React.forwardRef((props, ref) => {
  return <JarvisConnectionListForm {...props} forwardedRef={ref} />;
});

export const JarvisConnectionPanel = ({
  jarvisConnection,
  rows = 1,
  id = "jarvis-zugang",
}) => {
  const data = [];
  if (jarvisConnection) {
    data.push({
      ...jarvisConnection,
      status:
        jarvisConnection.status ||
        CONNECTION_STATUS_LABELS[jarvisConnection.status_id],
    });
  }

  const columnProps = {
    sortable: data.length > 1,
    filter: data.length > 10,
    filterMatchMode: "contains",
  };
  return (
    <PermissionResource resource={Resources.Profile.JarvisConnection}>
      <PermissionSection
        height="auto"
        id={id}
        className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
        isUnterWizardTitle
        idOffset={-90}
      >
        <h4 className="no-margin pad-big-top pad-btm">{"Jarvis-Zugang"}</h4>
        <DataTable
          value={data}
          rows={rows}
          emptyMessage={
            <TableEmptyMessage
              prefix="Kein"
              itemNamePlural="JARVIS-Zugang"
              min
            />
          }
        >
          <Column
            field="parentCompanyName"
            header="Vertragspartner"
            {...columnProps}
          />
          <Column
            field="permissions"
            header="Rechte"
            {...columnProps}
            body={(rowData) => getJarvisPermissionLabel(rowData.permissions)}
            style={{ width: "14em" }}
          />
          <Column
            field="status"
            header="Status"
            {...columnProps}
            style={{ width: "16em", textAlign: "center-" }}
          />
        </DataTable>
      </PermissionSection>
    </PermissionResource>
  );
};
JarvisConnectionPanel.propTypes = {
  jarvisConnection: PropTypes.shape({}).isRequired,
  rows: PropTypes.number,
};
